import { useState, useEffect } from "react";
import { 
  Users, 
  Plus, 
  Search,
  Phone,
  Mail,
  Instagram,
  ChevronRight,
  Trash2,
  Edit
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pipelineStages = [
  { value: "lead", label: "Lead", color: "#71717A" },
  { value: "contacted", label: "Contacted", color: "#3B82F6" },
  { value: "quote_sent", label: "Quote Sent", color: "#F59E0B" },
  { value: "booked", label: "Booked", color: "#8B5CF6" },
  { value: "in_progress", label: "In Progress", color: "#D4AF37" },
  { value: "delivered", label: "Delivered", color: "#22C55E" },
  { value: "paid", label: "Paid", color: "#10B981" },
  { value: "repeat_client", label: "Repeat Client", color: "#D4AF37" }
];

const Clients = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const [clientForm, setClientForm] = useState({
    name: "",
    phone: "",
    email: "",
    instagram: "",
    notes: "",
    pipeline_stage: "lead"
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API}/clients`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setClients(data?.clients ?? data ?? []);
      } else {
        setClients([]);
      }
    } catch (error) {
      toast.error("Failed to load clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async () => {
    try {
      const response = await fetch(`${API}/clients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(clientForm)
      });

      if (response.ok) {
        toast.success("Client added successfully");
        setShowAddDialog(false);
        setClientForm({ name: "", phone: "", email: "", instagram: "", notes: "", pipeline_stage: "lead" });
        fetchClients();
      }
    } catch (error) {
      toast.error("Failed to add client");
    }
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;
    try {
      const response = await fetch(`${API}/clients/${selectedClient.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(clientForm)
      });

      if (response.ok) {
        toast.success("Client updated");
        setShowEditDialog(false);
        setSelectedClient(null);
        fetchClients();
      }
    } catch (error) {
      toast.error("Failed to update client");
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try {
      await fetch(`${API}/clients/${id}`, {
        method: "DELETE",
        credentials: "include"
      });
      toast.success("Client deleted");
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleStageChange = async (clientId, newStage) => {
    try {
      await fetch(`${API}/clients/${clientId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pipeline_stage: newStage })
      });
      fetchClients();
      toast.success("Stage updated");
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setClientForm({
      name: client.name,
      phone: client.phone || "",
      email: client.email || "",
      instagram: client.instagram || "",
      notes: client.notes || "",
      pipeline_stage: client.pipeline_stage
    });
    setShowEditDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredClients = (clients || []).filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getClientsByStage = (stage) => (filteredClients || []).filter(c => c.pipeline_stage === stage);

  const getStageColor = (stage) => pipelineStages.find(s => s.value === stage)?.color || "#71717A";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Clients</h1>
          <p className="text-[#A1A1AA] mt-1">Manage your clients and CRM pipeline</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="add-client-btn">
              <Plus size={18} className="mr-2" /> Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-[#FAFAFA]">Add New Client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-[#A1A1AA]">Name *</Label>
                <Input
                  value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  placeholder="Client name"
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Phone</Label>
                  <Input
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                    placeholder="(xxx) xxx-xxxx"
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Email</Label>
                  <Input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    placeholder="email@example.com"
                    className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Instagram</Label>
                <Input
                  value={clientForm.instagram}
                  onChange={(e) => setClientForm({ ...clientForm, instagram: e.target.value })}
                  placeholder="@username"
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Pipeline Stage</Label>
                <Select value={clientForm.pipeline_stage} onValueChange={(v) => setClientForm({ ...clientForm, pipeline_stage: v })}>
                  <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                    {pipelineStages.map((stage) => (
                      <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Notes</Label>
                <Textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <Button onClick={handleAddClient} className="w-full btn-gold" data-testid="submit-client-btn">
                Add Client
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={20} />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search clients..."
          className="pl-10 bg-[#121214] border-[#27272A] text-[#FAFAFA]"
          data-testid="client-search"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pipeline" className="space-y-4">
        <TabsList className="bg-[#121214] border border-[#27272A]">
          <TabsTrigger value="pipeline" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Pipeline View
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            List View
          </TabsTrigger>
        </TabsList>

        {/* Pipeline View */}
        <TabsContent value="pipeline">
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {pipelineStages.map((stage) => (
                <div key={stage.value} className="pipeline-stage w-72">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }}></div>
                      <h3 className="font-medium text-[#FAFAFA]">{stage.label}</h3>
                    </div>
                    <span className="text-xs text-[#71717A] bg-[#1A1A1D] px-2 py-1 rounded">
                      {getClientsByStage(stage.value).length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {getClientsByStage(stage.value).map((client) => (
                      <div 
                        key={client.id}
                        className="pipeline-card"
                        onClick={() => openEditDialog(client)}
                        data-testid={`pipeline-card-${client.id}`}
                      >
                        <h4 className="font-medium text-[#FAFAFA]">{client.name}</h4>
                        {client.email && (
                          <p className="text-xs text-[#71717A] flex items-center gap-1 mt-1">
                            <Mail size={12} /> {client.email}
                          </p>
                        )}
                        {client.balance_due > 0 && (
                          <p className="text-xs text-[#D4AF37] mt-2">
                            Balance: {formatCurrency(client.balance_due)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* List View */}
        <TabsContent value="list">
          <Card className="bg-[#121214] border-[#27272A]">
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Stage</th>
                      <th>Total Spent</th>
                      <th>Balance Due</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => (
                      <tr key={client.id}>
                        <td>
                          <div>
                            <p className="font-medium text-[#FAFAFA]">{client.name}</p>
                            {client.instagram && (
                              <p className="text-xs text-[#71717A] flex items-center gap-1">
                                <Instagram size={12} /> {client.instagram}
                              </p>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="space-y-1">
                            {client.email && (
                              <p className="text-xs text-[#A1A1AA] flex items-center gap-1">
                                <Mail size={12} /> {client.email}
                              </p>
                            )}
                            {client.phone && (
                              <p className="text-xs text-[#A1A1AA] flex items-center gap-1">
                                <Phone size={12} /> {client.phone}
                              </p>
                            )}
                          </div>
                        </td>
                        <td>
                          <Select
                            value={client.pipeline_stage}
                            onValueChange={(v) => handleStageChange(client.id, v)}
                          >
                            <SelectTrigger 
                              className="w-32 h-8 text-xs border-none"
                              style={{ backgroundColor: `${getStageColor(client.pipeline_stage)}20`, color: getStageColor(client.pipeline_stage) }}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                              {pipelineStages.map((stage) => (
                                <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="text-[#22C55E]">{formatCurrency(client.total_spent)}</td>
                        <td className={client.balance_due > 0 ? "text-[#F59E0B]" : "text-[#71717A]"}>
                          {formatCurrency(client.balance_due)}
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openEditDialog(client)}
                              className="text-[#A1A1AA] hover:text-[#D4AF37]"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteClient(client.id)}
                              className="text-[#A1A1AA] hover:text-[#EF4444]"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#121214] border-[#27272A]">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA]">Edit Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-[#A1A1AA]">Name *</Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A1A1AA]">Phone</Label>
                <Input
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Email</Label>
                <Input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Instagram</Label>
              <Input
                value={clientForm.instagram}
                onChange={(e) => setClientForm({ ...clientForm, instagram: e.target.value })}
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Pipeline Stage</Label>
              <Select value={clientForm.pipeline_stage} onValueChange={(v) => setClientForm({ ...clientForm, pipeline_stage: v })}>
                <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                  {pipelineStages.map((stage) => (
                    <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Notes</Label>
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            <Button onClick={handleUpdateClient} className="w-full btn-gold">
              Update Client
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
